import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';

import { HashTag, Post, Comment, Reaction, Member } from '../../../../types/entity.types';
import { FileType, RadioOrSelectOption } from '../../../../types/common.types';
import { RootState } from '../../../../types/state/root.types';
import { MemberState } from '../../../../types/state/member.types';
import { TribeState } from '../../../../types/state/tribe.types';
import { SystemState } from '../../../../types/state/system.types';

import { getProfilePictureForMember } from '../../../../utils/util';
import { ARCHIVED, POST_SEARCH_OPTIONS, TRIBE_PAGE_SIZE } from '../../../../utils/constants';

import { requestDeletePost, requestUpdatePost, requestViewPosts } from '../../../../api/post/post.actions';
import { requestCreateReaction, requestDeleteReaction } from '../../../../api/reaction/reaction.actions';
import { requestViewComments } from '../../../../api/comment/comment.actions';
import { requestViewHashtagOccurrences } from '../../../../api/administration/administration.actions';
import { requestEvaluateSupportConversation, requestUpdatePostStatus } from '../../../../api/approval/approval.actions';

import { setPost } from '../../../../slices/programme/tribe/tribe.slice';

import TribePost from '../../../../components/screens/programme/tribe/tribe-post/TribePost';

type Props = {
  status: string;
  post: Post;
  hashtags?: HashTag[];
  refreshPost?: (externalReference: string) => void;
  approval?: boolean;
  searchType?: RadioOrSelectOption;
  hierarchy?: boolean;
  setComposing?: (value: boolean) => void;
};

const TribePostContainer = ({
  status,
  post,
  hashtags = [],
  refreshPost,
  setComposing,
  approval = false,
  searchType = POST_SEARCH_OPTIONS[0],
  hierarchy = false
}: Props): ReactElement => {
  const [files, setFiles] = useState<FileType[]>([]);
  const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);
  const [liked, setLiked] = useState<boolean>(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showAllComments, setShowAllComments] = useState<boolean>(false);

  const { reactions } = useSelector(({ tribe }: RootState): TribeState => tribe);
  const { members } = useSelector(({ member }: RootState): MemberState => member);
  const { user } = useSelector(({ system }: RootState): SystemState => system);

  const dispatch: Dispatch = useDispatch();

  const navigate: NavigateFunction = useNavigate();

  const { programmeExternalReference, memberExternalReference }: Readonly<Params> = useParams();

  useEffect((): void => {
    if (!approval) {
      dispatch(
        // @ts-expect-error TODO: Fix after adding types to comment actions
        requestViewComments({
          postExternalReference: post.externalReference,
          page: 0,
          size: showAllComments || hierarchy ? 999 : 2,
          onSuccess: (data: Comment[]): void => setComments(data)
        })
      );
    } else {
      setComments(post.comments ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, post, showAllComments, approval]);

  useEffect((): void => {
    setFiles((post.linkedMediaFiles as FileType[]) ?? []);
  }, [post.linkedMediaFiles]);

  useEffect((): void => {
    const profilePicture: string = getProfilePictureForMember(members, post.userName);
    setProfilePicture(profilePicture);
  }, [post, members]);

  useEffect((): void => {
    const filteredReactions: Reaction[] = reactions.filter(
      (reaction: Reaction): boolean => reaction.postExternalReference === post.externalReference
    );

    setLiked(filteredReactions.some((reaction: Reaction): boolean => reaction.reaction === 'like'));
  }, [reactions, post]);

  const pinPost = (): void => {
    if (status === ARCHIVED) return;

    const pinned: boolean = !post.pinned;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestUpdatePost({
        input: {
          ...post,
          postExternalReference: post.externalReference,
          pinned
        },
        onSuccess: (): void => refreshPost?.(post.externalReference as string)
      })
    );
  };

  const editPost = (): void => {
    dispatch(setPost(post));
    setComposing?.(true);
  };

  const deletePost = (): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestDeletePost({
        postExternalReference: post.externalReference,
        onSuccess: (): void => {
          dispatch(
            // @ts-expect-error TODO: Fix after adding types to post actions
            requestViewPosts({
              programmeExternalReference,
              page: 0,
              size: TRIBE_PAGE_SIZE,
              searchType: searchType?.value
            })
          );
          // @ts-expect-error TODO: Fix after adding types to administration actions
          dispatch(requestViewHashtagOccurrences({ programmeExternalReference }));
        }
      })
    );
  };

  const createReaction = (reaction: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      requestCreateReaction({
        input: {
          postExternalReference: post.externalReference,
          programmeExternalReference,
          reaction
        },
        onSuccess: (): void => refreshPost?.(post.externalReference as string)
      })
    );
  };

  const deleteReaction = (reaction: string): void => {
    const member: Member | undefined = members.find((member: Member): boolean => member.username === user?.username);

    if (!member) return;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      requestDeleteReaction({
        postExternalReference: post.externalReference,
        memberExternalReference: member.memberExternalReference,
        reaction,
        onSuccess: (): void => refreshPost?.(post.externalReference as string)
      })
    );
  };

  const updatePostStatus = (status: string, onSuccess: () => void): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to approval actions
      requestUpdatePostStatus({
        programmeExternalReference,
        postExternalReference: post.externalReference,
        status,
        onSuccess
      })
    );
  };

  const evaluateSupportConversation = (systemUserExternalReference: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to approval actions
      requestEvaluateSupportConversation({
        programmeExternalReference,
        systemUserExternalReference,
        onSuccess: (): void =>
          navigate(`/programmes/${status}/${programmeExternalReference}/support/${systemUserExternalReference}`)
      })
    );
  };

  const navigateToProfile = (username: string): void => {
    const member: Member | undefined = members.find((member: Member): boolean => member.username === username);

    if (!member || memberExternalReference === member.memberExternalReference) return;

    navigate(`/programmes/${status}/${programmeExternalReference}/tribe/${member.memberExternalReference}`);
  };

  return (
    <TribePost
      status={status}
      post={post}
      hashtags={hashtags}
      files={files}
      profilePicture={profilePicture}
      liked={liked}
      comments={comments}
      approval={approval}
      showAllComments={showAllComments}
      setShowAllComments={setShowAllComments}
      pinPost={pinPost}
      editPost={editPost}
      deletePost={deletePost}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      refreshPost={refreshPost}
      updatePostStatus={updatePostStatus}
      evaluateSupportConversation={evaluateSupportConversation}
      hierarchy={hierarchy}
      navigateToProfile={navigateToProfile}
    />
  );
};

export default TribePostContainer;
